import {TaskLeadingVisual} from '@github-ui/agent-sessions/components/TaskLeadingVisual'
import {AlertIcon, CheckCircleIcon, InfoIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {clsx} from 'clsx'
import type {FC} from 'react'
import styles from './FindingsSidebar.module.css'
import {fixTaskStatusMap} from './FixFindingStatus'
import type {MergeReadiness} from './types'
import type {FindingsView, SourceFilterOption, ToolFilterOption} from './use-filter-findings'
import {useFixTask} from './use-fix-task'

export interface SidebarFixTask {
  findingTitle: string
  ccaTaskId: string
  taskBasePath: string
}

interface FindingsSidebarProps {
  selectedView: FindingsView
  attentionCount: number
  otherCount: number
  ignoredCount: number
  outdatedCount: number
  onViewChange: (view: FindingsView) => void
  availableSources: SourceFilterOption[]
  selectedSources: ReadonlySet<string>
  onSourceToggle: (source: SourceFilterOption) => void
  availableTools: ToolFilterOption[]
  selectedTools: ReadonlySet<string>
  onToolToggle: (tool: ToolFilterOption) => void
  fixTasks: SidebarFixTask[]
  mergeReadiness?: MergeReadiness | null
}

export const FindingsSidebar: FC<FindingsSidebarProps> = ({
  selectedView,
  attentionCount,
  otherCount,
  ignoredCount,
  outdatedCount,
  onViewChange,
  availableSources,
  selectedSources,
  onSourceToggle,
  availableTools,
  selectedTools,
  onToolToggle,
  fixTasks,
  mergeReadiness,
}) => {
  const hasSourceFilters = availableSources.length > 0
  const hasToolFilters = availableTools.length > 0

  return (
    <aside className={styles.sidebar} aria-label="Findings sidebar">
      {mergeReadiness && <MergeReadinessSection readiness={mergeReadiness} />}
      <ActionList role="menu" aria-label="View" selectionVariant="single" className="tmp-py-0">
        <ActionList.Group selectionVariant="single">
          <ActionList.GroupHeading>View</ActionList.GroupHeading>
          <ActionList.Item
            role="menuitemradio"
            selected={selectedView === 'attention'}
            onSelect={() => onViewChange('attention')}
          >
            Needs attention
            <ActionList.TrailingVisual>{attentionCount}</ActionList.TrailingVisual>
          </ActionList.Item>
          <ActionList.Item
            role="menuitemradio"
            selected={selectedView === 'open'}
            onSelect={() => onViewChange('open')}
          >
            Lower-severity
            <ActionList.TrailingVisual>{otherCount}</ActionList.TrailingVisual>
          </ActionList.Item>
          {(ignoredCount > 0 || selectedView === 'resolved') && (
            <ActionList.Item
              role="menuitemradio"
              selected={selectedView === 'resolved'}
              onSelect={() => onViewChange('resolved')}
            >
              Resolved
              <ActionList.TrailingVisual>{ignoredCount}</ActionList.TrailingVisual>
            </ActionList.Item>
          )}
          {(outdatedCount > 0 || selectedView === 'outdated') && (
            <ActionList.Item
              role="menuitemradio"
              selected={selectedView === 'outdated'}
              onSelect={() => onViewChange('outdated')}
            >
              Outdated
              <ActionList.TrailingVisual>{outdatedCount}</ActionList.TrailingVisual>
            </ActionList.Item>
          )}
        </ActionList.Group>
      </ActionList>
      {hasSourceFilters && (
        <ActionList role="menu" aria-label="Sources" selectionVariant="multiple" className="tmp-py-0">
          <ActionList.Group selectionVariant="multiple">
            <ActionList.GroupHeading>Sources</ActionList.GroupHeading>
            {availableSources.map(source => (
              <ActionList.Item
                key={source.key}
                role="menuitemcheckbox"
                selected={selectedSources.has(source.key)}
                onSelect={() => onSourceToggle(source)}
              >
                {source.label}
              </ActionList.Item>
            ))}
          </ActionList.Group>
        </ActionList>
      )}
      {hasToolFilters && (
        <ActionList role="menu" aria-label="Jobs" selectionVariant="multiple" className="tmp-py-0">
          <ActionList.Group selectionVariant="multiple">
            <ActionList.GroupHeading>Jobs</ActionList.GroupHeading>
            {availableTools.map(tool => (
              <ActionList.Item
                key={tool.key}
                role="menuitemcheckbox"
                selected={selectedTools.has(tool.key)}
                onSelect={() => onToolToggle(tool)}
              >
                {tool.label}
              </ActionList.Item>
            ))}
          </ActionList.Group>
        </ActionList>
      )}
      {fixTasks.length > 0 && (
        <ActionList className="tmp-py-0">
          <ActionList.Divider />
          <ActionList.Group>
            <ActionList.GroupHeading as="h3" className="tmp-mt-n1">
              Agent sessions
            </ActionList.GroupHeading>
            {fixTasks.map(task => (
              <SidebarFixTaskItem key={task.ccaTaskId} {...task} />
            ))}
          </ActionList.Group>
        </ActionList>
      )}
    </aside>
  )
}

const readinessConfig: Record<MergeReadiness, {icon: typeof CheckCircleIcon; label: string; color: string}> = {
  ready: {icon: CheckCircleIcon, label: 'Ready to approve', color: 'color-fg-success'},
  requires_changes: {icon: AlertIcon, label: 'Not ready to approve', color: 'color-fg-attention'},
  too_complex: {icon: AlertIcon, label: 'Human review recommended', color: 'color-fg-attention'},
  unknown: {icon: InfoIcon, label: 'Unable to assess', color: 'color-fg-muted'},
}

const MergeReadinessSection: FC<{readiness: MergeReadiness}> = ({readiness}) => {
  const config = readinessConfig[readiness]
  const Icon = config.icon

  return (
    <div className={clsx('d-flex flex-items-center gap-2 px-3 py-2', styles.readinessSection, config.color)}>
      <Icon size={16} />
      <span className="text-small text-semibold">{config.label}</span>
    </div>
  )
}

const SidebarFixTaskItem: FC<SidebarFixTask> = ({findingTitle, ccaTaskId, taskBasePath}) => {
  const {fixTask, taskUrl} = useFixTask({ccaTaskId, taskBasePath})
  if (!fixTask || !taskUrl) return null

  const status = fixTaskStatusMap[fixTask.state]
  if (!status) return null

  return (
    <ActionList.Item as="a" href={taskUrl}>
      <ActionList.LeadingVisual>
        <TaskLeadingVisual sessionState={fixTask.state} />
      </ActionList.LeadingVisual>
      <span className={styles.taskContent}>
        <span className={clsx('text-small text-semibold', status.color)}>{status.label}</span>
        <span className={clsx('text-small', styles.taskTitle)}>{findingTitle}</span>
      </span>
    </ActionList.Item>
  )
}
